import { QuoteRow } from '../types';

/** Virtual filter value for quote list — not a quotes.status column value. */
export const QUOTE_APPOINTMENT_CANCELED_FILTER = 'APPOINTMENT_CANCELED';
export const QUOTE_APPOINTMENT_CREATED_FILTER = 'APPOINTMENT_CREATED';
export const QUOTE_PENDING_DEPOSIT_FILTER = 'PENDING_DEPOSIT';
export const QUOTE_OFFER_EXPIRED_FILTER = 'OFFER_EXPIRED';

export const isQuoteOfferExpired = (quote: QuoteRow): boolean =>
  quote.status === 'BUSINESS_OFFER_SENT' &&
  quote.offerExpiredAt != null &&
  !Number.isNaN(new Date(quote.offerExpiredAt).getTime()) &&
  new Date(quote.offerExpiredAt).getTime() <= Date.now();

export const formatQuoteStatusLabel = (status: string | null | undefined): string => {
  if (!status) return '—';
  if (status === 'NEW') return 'Pending Review';
  if (status === 'BUSINESS_OFFER_SENT') return 'Offer Sent';
  if (status === 'BUSINESS_OFFER_ACCEPTED') return 'Offer Accepted';
  if (status === 'DEPOSIT_RECEIVED') return 'Deposit Received';
  return status
    .replace(/_/g, ' ')
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase());
};

export const getQuoteStatusBadgeClass = (quote: QuoteRow): string => {
  if (isQuoteOfferExpired(quote)) return 'bg-amber-100 text-amber-800 border border-amber-200';
  switch ((quote.status || '').toUpperCase()) {
    case 'BUSINESS_OFFER_ACCEPTED':
    case 'CUSTOMER_OFFER_ACCEPTED':
    case 'DEPOSIT_RECEIVED':
      return 'bg-green-100 text-green-800';
    case 'NEW':
      return 'bg-blue-100 text-blue-800';
    default:
      return 'bg-gray-100 text-gray-800';
  }
};

export const getQuoteDisplayStatus = (quote: QuoteRow): { label: string; badgeClass: string } => {
  if (isQuoteOfferExpired(quote)) {
    return { label: 'Offer Expired', badgeClass: 'bg-amber-100 text-amber-800 border border-amber-200' };
  }
  const appointmentStatus = (quote.appointmentStatus || '').toUpperCase();
  if (quote.appointmentId && appointmentStatus === 'CANCELLED') {
    return { label: 'Appointment Canceled', badgeClass: 'bg-red-100 text-red-800 border border-red-200' };
  }
  if ((quote.status || '').toUpperCase() === 'DEPOSIT_RECEIVED') {
    return { label: 'Deposit Received', badgeClass: 'bg-green-100 text-green-800' };
  }
  if (quote.appointmentId && appointmentStatus === 'PENDING') {
    return { label: 'Pending Deposit', badgeClass: 'bg-amber-100 text-amber-800 border border-amber-200' };
  }
  if (quote.appointmentId && appointmentStatus !== 'CANCELLED') {
    return { label: 'Appointment Created', badgeClass: 'bg-teal-100 text-teal-800 border border-teal-200' };
  }
  return { label: formatQuoteStatusLabel(quote.status), badgeClass: getQuoteStatusBadgeClass(quote) };
};

export const getCustomerQuoteStatusBadgeClass = (quote: QuoteRow): string => {
  if (isQuoteOfferExpired(quote)) return 'bg-amber-100 text-amber-800 border border-amber-200';
  const appointmentStatus = (quote.appointmentStatus || '').toUpperCase();
  if (quote.appointmentId && appointmentStatus === 'CANCELLED') {
    return 'bg-red-100 text-red-800 border border-red-200';
  }
  if (quote.appointmentId && appointmentStatus === 'PENDING') {
    return 'bg-amber-100 text-amber-800 border border-amber-200';
  }
  if (quote.appointmentId && appointmentStatus !== 'CANCELLED' && appointmentStatus !== 'PENDING') {
    return 'bg-teal-100 text-teal-800 border border-teal-200';
  }
  switch ((quote.status || '').toUpperCase()) {
    case 'BUSINESS_OFFER_ACCEPTED':
      return 'bg-green-100 text-green-800';
    case 'NEW':
    case 'PENDING':
      return 'bg-blue-100 text-blue-800';
    case 'ACCEPTED':
      return 'bg-green-100 text-green-800';
    case 'DECLINED':
      return 'bg-red-100 text-red-800';
    default:
      return 'bg-gray-100 text-gray-800';
  }
};

/** Customer-facing quote status labels for list and detail views. */
export const getCustomerQuoteDisplayStatus = (quote: QuoteRow): { label: string; badgeClass: string } => {
  if (isQuoteOfferExpired(quote)) {
    return { label: 'Offer Expired', badgeClass: 'bg-amber-100 text-amber-800 border border-amber-200' };
  }
  const appointmentStatus = (quote.appointmentStatus || '').toUpperCase();
  if (quote.appointmentId && appointmentStatus === 'CANCELLED') {
    return { label: 'Appointment Cancelled', badgeClass: 'bg-red-100 text-red-800 border border-red-200' };
  }
  if (quote.appointmentId && appointmentStatus === 'PENDING') {
    return { label: 'Pending Deposit', badgeClass: 'bg-amber-100 text-amber-800 border border-amber-200' };
  }
  if (quote.appointmentId && appointmentStatus !== 'CANCELLED' && appointmentStatus !== 'PENDING') {
    return { label: 'Appointment Created', badgeClass: 'bg-teal-100 text-teal-800 border border-teal-200' };
  }
  if (quote.status === 'NEW' || quote.status === 'PENDING') {
    return { label: 'Request Submitted', badgeClass: 'bg-blue-100 text-blue-800' };
  }
  if (quote.status === 'BUSINESS_OFFER_SENT') {
    return { label: 'Offer Received', badgeClass: 'bg-gray-100 text-gray-800' };
  }
  if (quote.status === 'BUSINESS_OFFER_ACCEPTED') {
    return { label: 'Offer Accepted', badgeClass: 'bg-green-100 text-green-800' };
  }
  const label = (quote.status || '')
    .replace(/_/g, ' ')
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase());
  return { label: label || '—', badgeClass: getCustomerQuoteStatusBadgeClass(quote) };
};
